import { useState } from "react"
import { DollarSign, TrendingUp, ShoppingCart, Percent, AlertCircle, Save } from "lucide-react"
import {
  useGetCommissionAnalyticsQuery,
  useGetCommissionByPeriodQuery,
  useGetCommissionByBusinessQuery,
  useUpdateCommissionRateMutation,
} from "../features/admin/adminApi"

function formatCurrency(amount: number, currency = "NGN") {
  const symbol = currency === "NGN" ? "₦" : currency
  return `${symbol}${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

export default function CommissionPage() {
  const [period, setPeriod] = useState<"day" | "week" | "month" | "year">("month")
  const [newRate, setNewRate] = useState("")
  const [showRateForm, setShowRateForm] = useState(false)

  const { data: analytics, isLoading: analyticsLoading } = useGetCommissionAnalyticsQuery()
  const { data: periodData, isLoading: periodLoading } = useGetCommissionByPeriodQuery({ period, limit: 12 })
  const { data: topBusinesses, isLoading: businessesLoading } = useGetCommissionByBusinessQuery({ limit: 10 })
  const [updateRate, { isLoading: updating }] = useUpdateCommissionRateMutation()

  const handleUpdateRate = async (e: React.FormEvent) => {
    e.preventDefault()
    const rateValue = parseFloat(newRate)
    if (isNaN(rateValue) || rateValue < 0 || rateValue > 100) {
      alert("Please enter a valid percentage between 0 and 100")
      return
    }

    try {
      await updateRate({ rate: rateValue / 100 }).unwrap()
      alert("Commission rate updated successfully!")
      setShowRateForm(false)
      setNewRate("")
    } catch (err: any) {
      alert(err?.data?.message || "Failed to update commission rate")
    }
  }

  if (analyticsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading commission analytics...</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Commission & Revenue</h1>
        <p className="text-gray-600 mt-1">Platform earnings from business transactions</p>
      </div>

      {/* Analytics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Commission</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {formatCurrency(analytics?.totalCommission || 0)}
              </p>
            </div>
            <div className="bg-blue-100 p-3 rounded-lg">
              <DollarSign className="text-blue-600" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Business Payouts</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {formatCurrency(analytics?.totalBusinessPayout || 0)}
              </p>
            </div>
            <div className="bg-green-100 p-3 rounded-lg">
              <TrendingUp className="text-green-600" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Orders</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {analytics?.orderCount?.toLocaleString() || 0}
              </p>
            </div>
            <div className="bg-purple-100 p-3 rounded-lg">
              <ShoppingCart className="text-purple-600" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-orange-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Commission Rate</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {((analytics?.currentCommissionRate || 0) * 100).toFixed(1)}%
              </p>
            </div>
            <div className="bg-orange-100 p-3 rounded-lg">
              <Percent className="text-orange-600" size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Commission Rate Configuration */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Commission Rate Configuration</h2>
              <p className="text-sm text-gray-600 mt-1">
                Current rate: <span className="font-semibold">{((analytics?.currentCommissionRate || 0) * 100).toFixed(2)}%</span>
              </p>
            </div>
            {!showRateForm && (
              <button
                onClick={() => {
                  setShowRateForm(true)
                  setNewRate(((analytics?.currentCommissionRate || 0) * 100).toFixed(2))
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Update Rate
              </button>
            )}
          </div>
        </div>

        {showRateForm && (
          <div className="p-6 bg-gray-50">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4 flex items-start gap-3">
              <AlertCircle className="text-yellow-600 flex-shrink-0 mt-0.5" size={20} />
              <div className="text-sm text-yellow-800">
                <strong>Warning:</strong> Changing the commission rate will only affect new orders.
                Existing orders will retain their original commission rate.
              </div>
            </div>

            <form onSubmit={handleUpdateRate} className="max-w-md">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Commission Rate (%)
              </label>
              <div className="flex gap-3">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={newRate}
                  onChange={(e) => setNewRate(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., 10.00"
                  required
                />
                <button
                  type="submit"
                  disabled={updating}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  <Save size={16} />
                  {updating ? "Saving..." : "Save"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowRateForm(false)
                    setNewRate("")
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Example: Enter 10 for 10%, 15.5 for 15.5%, etc.
              </p>
            </form>
          </div>
        )}
      </div>

      {/* Revenue Trends */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Revenue Trends</h2>
            <div className="flex gap-2">
              {(["day", "week", "month", "year"] as const).map((p) => (
                <button
                  key={p}
                  onClick={() => setPeriod(p)}
                  className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                    period === p
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {p.charAt(0).toUpperCase() + p.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="p-6">
          {periodLoading ? (
            <div className="text-center py-8 text-gray-500">Loading trend data...</div>
          ) : !periodData || periodData.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No data available for this period</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Period
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Commission
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Business Payout
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Revenue
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Orders
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {periodData.map((row, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{row.period}</td>
                      <td className="px-4 py-3 text-sm text-right text-green-600 font-semibold">
                        {formatCurrency(row.commission)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-gray-600">
                        {formatCurrency(row.businessPayout)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900 font-medium">
                        {formatCurrency(row.revenue)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-gray-600">
                        {row.orderCount.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Top Businesses by Commission */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Top Businesses by Commission</h2>
          <p className="text-sm text-gray-600 mt-1">Businesses generating the most platform revenue</p>
        </div>

        <div className="p-6">
          {businessesLoading ? (
            <div className="text-center py-8 text-gray-500">Loading business data...</div>
          ) : !topBusinesses || topBusinesses.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No business data available</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rank
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Business Name
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Commission Earned
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Business Payout
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Orders
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {topBusinesses.map((biz, idx) => (
                    <tr key={biz.businessId} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-medium">#{idx + 1}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{biz.businessName || "Unknown"}</td>
                      <td className="px-4 py-3 text-sm text-right text-green-600 font-semibold">
                        {formatCurrency(biz.commission)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-gray-600">
                        {formatCurrency(biz.businessPayout)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-gray-600">
                        {biz.orderCount.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
